import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

const SplashContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  width: '100vw',
  backgroundColor: theme.palette.background.default,
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 9999,
}));

interface LogoImageProps {
  isLogoVisible: boolean;
}

const LogoImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'isLogoVisible',
})<LogoImageProps>(({ theme, isLogoVisible }) => ({
  maxWidth: 'clamp(160px, 44vw, 220px)',
  maxHeight: 'clamp(160px, 44vh, 220px)',
  objectFit: 'contain',
  display: isLogoVisible ? 'block' : 'none',
  [theme.breakpoints.up('sm')]: {
    maxWidth: 'clamp(120px, 24vw, 240px)',
    maxHeight: 'clamp(120px, 24vh, 240px)',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: 'clamp(140px, 18vw, 280px)',
    maxHeight: 'clamp(140px, 18vh, 280px)',
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: 'clamp(160px, 14vw, 320px)',
    maxHeight: 'clamp(160px, 14vh, 320px)',
  },
}));

interface HospitalLogoSplashProps {
  logoUrl?: string;
  altText?: string;
  showSpinner?: boolean;
  isLogoVisible?: boolean;
  onLogoLoad?: () => void;
  onLogoError?: () => void;
}

/**
 * Presentational component for displaying hospital logo splash screen.
 * Timer management is handled by the useSplash hook in parent components.
 */
export const HospitalLogoSplash = ({
  logoUrl,
  altText = 'Hospital logo',
  showSpinner = true,
  isLogoVisible = false,
  onLogoLoad,
  onLogoError,
}: HospitalLogoSplashProps) => (
  <SplashContainer>
    {showSpinner && <CircularProgress size={60} />}
    {logoUrl ? (
      <LogoImage
        src={logoUrl}
        alt={altText}
        onLoad={onLogoLoad}
        onError={onLogoError}
        isLogoVisible={isLogoVisible}
      />
    ) : null}
  </SplashContainer>
);
